/**
 * Adding a signatory to a draft agreement.
 *
 * ===========================================================================
 * WHY A DRAFT WITHOUT ONE IS STRANDED
 * ===========================================================================
 * Everything downstream is keyed on recipients. Sending needs somebody to send to;
 * execution is recorded against a NAMED recipient, never "whoever is next"; and
 * countersigning is refused until every required signatory has accepted. So a draft
 * with no recipients cannot be sent, signed, countersigned, or converted — it simply
 * sits there, and nothing on the screen says why.
 *
 * The screens that read recipients all existed. Nothing created one.
 *
 * ===========================================================================
 * WHY THIS IS ONLY AVAILABLE ON A DRAFT
 * ===========================================================================
 * Recipients are fixed when the agreement is sent. That is the server's rule, not this
 * screen's: sending freezes the text and issues it, and adding a signatory afterwards
 * would mean somebody is bound by a document they were not party to when it was
 * issued. The control is hidden rather than disabled once sent, because there is no
 * state in which it becomes available again.
 *
 * ===========================================================================
 * WHY THE FIELDS PRE-FILL FROM THE LEAD, AND ONLY SOMETIMES
 * ===========================================================================
 * An agreement drafted from a lead already has the enquirer's name and email on
 * file — retyping them here is copying a value the record already holds, the exact
 * thing this product elsewhere goes out of its way to avoid (see the lead conversion
 * plan's "nothing is retyped" tests). So when the lead is an *individual*, the form
 * opens pre-filled with their name and email.
 *
 * Only when it is an individual. An organisation's signatory is a person AT the
 * company, never the company itself — pre-filling "Northgate Holdings Ltd" into a
 * signatory field would be actively wrong, not just unhelpful, so an organisation
 * lead opens this form exactly as blank as it always was.
 *
 * Still editable either way, and still an explicit act: nothing here adds a
 * signatory automatically. Pre-filling saves the retyping; it does not remove the
 * decision.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type Agreement,
  type Lead,
} from '@legalcrm/api';
import {
  Checkbox,
  Form,
  FormActions,
  FormInput,
  Modal,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

const schema = z.object({
  display_name: z.string().min(1, 'Who is signing?'),
  email: z.string().email('Enter a valid email address').or(z.literal('')),
});

type Values = z.infer<typeof schema>;

export function AddRecipientDialog({
  agreement,
  open,
  onClose,
}: {
  agreement: Agreement;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  // Only fetched when there is a lead to read and the dialog is actually open — no
  // point holding a query alive against a client-originated agreement, which has no
  // lead at all (`client` and `lead` are mutually exclusive per CR-01).
  const lead = useApiQuery<Lead>(qk.detail('leads', agreement.lead ?? ''), STAFF.lead(agreement.lead ?? ''), {
    enabled: open && Boolean(agreement.lead),
  });
  const prefillFrom =
    lead.data?.lead_type === 'individual'
      ? { display_name: lead.data.full_name, email: lead.data.email }
      : { display_name: '', email: '' };

  /**
   * Required by default, and a deliberate choice rather than a convenience.
   *
   * A required signatory blocks countersigning until they accept. Somebody added as
   * optional by accident is a person the firm believes has agreed and who never had to
   * — which is the failure worth defending against, so the safe value is the default
   * and making somebody optional is the explicit act.
   */
  const [isRequired, setIsRequired] = useState(true);

  // `values`, not `defaultValues`: the lead query resolves after the dialog has
  // already mounted (it opens from a click, the fetch follows), so the prefill has to
  // land once the response arrives, not just once at mount when it would still be
  // blank.
  const form = useZodForm(schema, {
    values: prefillFrom,
  });

  const add = useApiMutation<Agreement, Values>(
    {
      mutationFn: (values) =>
        api.post<Agreement>(`${STAFF.agreementRecipients(agreement.id)}/`, {
          display_name: values.display_name,
          // Absent rather than empty: "" would record that this person has no address
          // rather than that nobody has given one yet.
          email: values.email || undefined,
          is_required: isRequired,
        }),
      invalidates: [qk.all('agreements')],
    },
    {
      onSuccess: () => {
        toast({ title: 'Signatory added', tone: 'success' });
        form.reset();
        onClose();
      },
      onError: (error) =>
        toast({
          title: 'That signatory was not added',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'Recipients can only be added while the agreement is a draft.',
          tone: 'warning',
        }),
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a signatory"
      description="Who has to sign this agreement. They are fixed once it is sent."
    >
      <Form
        form={form}
        ariaLabel="Add a signatory"
        error={add.error}
        onSubmit={(values) => add.mutateAsync(values)}
      >
        <FormInput
          form={form}
          name="display_name"
          label="Name"
          autoFocus
          autoComplete="off"
        />
        <FormInput
          form={form}
          name="email"
          label="Email"
          type="email"
          hint="Where the agreement is sent. Without one they can only sign in person."
        />
        <Checkbox
          label="Their signature is required"
          checked={isRequired}
          onChange={(event) => setIsRequired(event.currentTarget.checked)}
          hint="The firm cannot countersign until every required signatory has accepted."
        />
        <FormActions
          submitLabel="Add signatory"
          busy={add.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
